import { createSelectorHooks } from 'auto-zustand-selectors-hook';
import { RefObject, createRef } from 'react';
import { create } from 'zustand';

import { EditorActions } from '$/pages/EditorPage/hooks/useEditorActions';
import { EditorModuleStatus } from '$/services/usecases/editor/mapper/editorStatus';
import { HoverStatus } from '$/services/usecases/editor/mapper/hoverStatus';

export const MAXZOOMLEVEL = 25600;
export const MINZOOMLEVEL = 1;

export type SelectMode = 'single' | 'multiple' | 'bulk';

type InternalState = {
  rawStatus: object;
  editorModuleRef: RefObject<HTMLIFrameElement>;
  isEditorModuleLoading: boolean;
  isProjectLoading: boolean;
  hoveredObject: HoverStatus;
  openedAccordionIndices: number[];
};

type Actions = {
  syncState: (status: EditorModuleStatus, rawStatus: object) => void;
  setIsEditorModuleLoading: (isLoading: boolean) => void;
  setIsProjectLoading: (isLoading: boolean) => void;
  setComponentActive: (actions: EditorActions, componentIndex: number) => void;
  setLayerActive: (
    actions: EditorActions,
    layerIndex: number,
    parentIndex: number,
  ) => void;
  setZoomLevel: (newZoomLevel: number) => void;
  resetSelection: () => void;
  resetStore: () => void;
  setMaterial: (componentIndex: number, materialId: string) => void;
  setHoveredObject: (
    elementIndex: HoverStatus['elementIndex'],
    layerIndex: HoverStatus['layerIndex'],
  ) => void;
  setOpenAccordionOpenIndices: (indices: number[]) => void;
  moveAccordionItem: (fromIndex: number, toIndex: number) => void;
  shiftAccordionItems: () => void;
  deleteAccordionItem: (index: number) => void;
};

const initial: InternalState & EditorModuleStatus = {
  rawStatus: {},
  components: [],
  editorModuleRef: createRef<HTMLIFrameElement>(),
  isEditorModuleLoading: true,
  isProjectLoading: true,
  activeComponentIndex: null,
  activeLayerIndex: null,
  brushSize: 0,
  brushType: 'round',
  limitToMask: false,
  rubberSize: 0,
  rubberType: 'round',
  workMode: 'select',
  zoomPercent: 100,
  zoomType: 'zoomPercent',
  hoveredObject: {
    elementIndex: null,
    layerIndex: null,
  },
  colorCheckerEnabled: false,
  openedAccordionIndices: [],
};

export const getLetterFromIndex = (number: number): string => {
  let result = '';

  while (number > 0) {
    const remainder = (number - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    number = Math.floor((number - 1) / 26);
  }

  return result;
};

export const store = create<InternalState & Actions & EditorModuleStatus>()(
  (set, get) => ({
    ...initial,
    syncState: (status, rawStatus) => {
      set({ ...status, rawStatus });
    },
    setIsEditorModuleLoading: (isLoading) => {
      set({ isEditorModuleLoading: isLoading });
    },
    setIsProjectLoading: (isLoading) => {
      set({ isProjectLoading: isLoading });
    },
    setComponentActive: (actions, componentId) => {
      actions.selectComponent(componentId);
    },
    setLayerActive: (actions, layerIndex, parentIndex) => {
      actions.selectObjects([
        { componentIndex: parentIndex, objectIndex: layerIndex },
      ]);
    },
    setZoomLevel: (newZoomLevel) => {
      set({ zoomPercent: newZoomLevel });
    },
    resetSelection: () => {
      set({ activeComponentIndex: null, activeLayerIndex: null });
    },
    resetStore: () => set(initial),
    setMaterial: (componentIndex, materialId) => {
      const components = [...get().components];
      components[componentIndex].materialId = materialId;

      set({ components });
    },
    setHoveredObject: (elementIndex, layerIndex) => {
      set({
        hoveredObject: {
          elementIndex,
          layerIndex,
        },
      });
    },
    setOpenAccordionOpenIndices: (indices) => {
      set({ openedAccordionIndices: indices });
    },
    shiftAccordionItems: () => {
      const newArray = get().openedAccordionIndices.map((index) => index + 1);
      set({ openedAccordionIndices: newArray });
    },
    moveAccordionItem: (fromIndex, toIndex) => {
      const newArray = get().openedAccordionIndices.map((index) => {
        // move to new position
        if (index === fromIndex) {
          return toIndex;
        }

        // everything in between should be moved one position when it's between from and to
        if (fromIndex < toIndex) {
          // everything in between should be moved one position when it's between from and to

          if (index > fromIndex && index <= toIndex) {
            return index - 1;
          }
        } else if (index >= toIndex && index < fromIndex) {
          return index + 1;
        }

        return index;
      });

      set({ openedAccordionIndices: newArray });
    },
    deleteAccordionItem: (deletedIndex) => {
      const newArray = get()
        .openedAccordionIndices.filter((index) => index !== deletedIndex)
        .map((item) => (item > deletedIndex ? item - 1 : item));

      set({ openedAccordionIndices: newArray });
    },
  }),
);

export const useEditorStore = createSelectorHooks(store);
